import React, { useState } from 'react';
import { useBasicPredictions } from '../../hooks/useBasicPredictions';
import { BasicScoreCard } from './BasicScoreCard';
import { BasicHistoryChart } from './BasicHistoryChart';
import { ScenarioBuilder } from './ScenarioBuilder';
import { TrajectoryVisualization } from './TrajectoryVisualization';
import { BASIC_CATEGORIES, getBasicName, type BasicId, type BasicPrediction } from '../../types/predictions';
import { AlertCircle, RefreshCw, TrendingUp, Shield, Info, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface BasicPredictionsTabProps {
  dotNumber: number;
}

/**
 * BASIC Predictions Tab Component
 * Main container for displaying all BASIC score predictions
 *
 * Features:
 * - Displays all 7 BASIC scores (11, 12, 13, 14, 15, 16, 17)
 * - Loading and error states
 * - Refresh functionality
 * - Responsive grid layout
 * - Summary statistics
 * - Help modal with comprehensive explanations
 * - Tooltips for key metrics
 */
export const BasicPredictionsTab: React.FC<BasicPredictionsTabProps> = ({ dotNumber }) => {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const { predictions, loading, error, refetch } = useBasicPredictions(dotNumber, selectedDate);
  const [showHelp, setShowHelp] = useState(false);
  const [expandedBasicId, setExpandedBasicId] = useState<BasicId | null>(null);

  const HelpModal = () => (
    <div className="fixed inset-0 bg-black/50 items-center justify-center z-[9999] p-5" style={{ display: showHelp ? 'flex' : 'none' }} onClick={() => setShowHelp(false)}>
      <div className="bg-white rounded-xl max-w-3xl max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="mt-0">Understanding BASIC Scores</h2>

        <h3>What are BASIC Scores?</h3>
        <p>
          Behavioral Analysis and Safety Improvement Categories (BASICs) are FMCSA's method of quantifying
          carrier safety performance. There are 7 categories covering different aspects of safety compliance.
        </p>

        <h3>How to Read BASIC Scores:</h3>
        <ul>
          <li><strong>Percentile (0-100):</strong> Your ranking among peer carriers. Lower is better! 0 = safest, 100 = most violations.</li>
          <li><strong>Measure:</strong> Raw score calculated from violations, inspections, and fleet size.</li>
          <li><strong>Threshold:</strong> FMCSA intervention point. Exceeding this triggers enforcement attention.</li>
          <li><strong>Confidence:</strong> How reliable the score is (based on data quality and quantity).</li>
        </ul>

        <h3>The 7 BASIC Categories:</h3>
        {(Object.entries(BASIC_CATEGORIES) as [BasicId, typeof BASIC_CATEGORIES[BasicId]][]).map(([id, cat]) => (
          <div key={id} className="mb-3 p-3 bg-gray-50 rounded-md">
            <strong>{id} - {cat.name}</strong> (Threshold: {cat.threshold}%)
            <br />
            <span className="text-[13px] text-gray-500">{cat.description}</span>
            {'note' in cat && (
              <div className="mt-1 text-xs text-red-500 italic">
                Note: {(cat as { note: string }).note}
              </div>
            )}
          </div>
        ))}

        <h3>Status Colors:</h3>
        <div className="flex gap-3 flex-wrap mb-4">
          <div className="px-3 py-1.5 bg-green-100 text-green-900 rounded text-[13px]">
            <strong>EXCELLENT:</strong> 0-20%
          </div>
          <div className="px-3 py-1.5 bg-blue-100 text-blue-900 rounded text-[13px]">
            <strong>SAFE:</strong> 20-threshold
          </div>
          <div className="px-3 py-1.5 bg-yellow-100 text-yellow-900 rounded text-[13px]">
            <strong>WARNING:</strong> threshold-85%
          </div>
          <div className="px-3 py-1.5 bg-red-100 text-red-900 rounded text-[13px]">
            <strong>CRITICAL:</strong> 85-100%
          </div>
        </div>

        <button
          onClick={() => setShowHelp(false)}
          className="px-5 py-2.5 bg-blue-500 text-white border-none rounded-md cursor-pointer text-sm font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-gray-600 text-lg">Loading BASIC predictions...</p>
        <p className="text-gray-400 text-sm mt-2">Calculating safety scores</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Error Loading Predictions
              </h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg
                         hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!predictions) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Shield className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-600 text-lg">No prediction data available</p>
        <p className="text-gray-400 text-sm mt-2">
          BASIC predictions could not be calculated for this carrier
        </p>
      </div>
    );
  }

  // Calculate summary statistics
  const calculateSummary = () => {
    // Support both V1 (predictions) and V2 (basics) response structures
    const basicsData = predictions.basics || predictions.predictions || {};
    const predictionsList = Object.entries(basicsData) as [string, BasicPrediction][];
    const statusCounts = {
      EXCELLENT: 0,
      SAFE: 0,
      WARNING: 0,
      CRITICAL: 0,
      GOOD: 0,
      INSUFFICIENT: 0,
      ELIMINATED: 0,
    };

    predictionsList.forEach(([, pred]) => {
      if (statusCounts.hasOwnProperty(pred.status)) {
        statusCounts[pred.status as keyof typeof statusCounts]++;
      }
    });

    const averagePercentile =
      predictionsList.reduce((sum, [, pred]) => sum + (pred.percentile || 0), 0) /
      Math.max(predictionsList.length, 1);

    const atRisk = (statusCounts.WARNING || 0) + (statusCounts.CRITICAL || 0);

    return {
      statusCounts,
      averagePercentile,
      atRisk,
      total: predictionsList.length,
    };
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">BASIC Score Predictions</h2>
              <button
                onClick={() => setShowHelp(true)}
                className="bg-transparent border border-gray-300 rounded-md px-3 py-1.5 cursor-pointer flex items-center gap-1 text-[13px] text-gray-500"
              >
                <HelpCircle className="w-4 h-4" />
                Help
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Behavioral Analysis and Safety Improvement Categories - Predictive safety scores
              calculated from historical violation data
            </p>

            {/* Info banner - compact single line */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-[13px] text-blue-900">
              <strong>BASIC Scores:</strong> Lower is better (0-100 scale). Thresholds: 65% (Unsafe/HOS/Crash) | 80% (Fitness/Drugs/Maint/Hazmat). Calculated using exact FMCSA SMS methodology (September 2025 PDF) with real-time data.
            </div>
          </div>

          {/* Historical Date Picker + Refresh */}
          <div className="ml-4 flex items-center gap-3">
            {/* Date Picker */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600 uppercase">
                Calculate As Of:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={selectedDate || new Date().toISOString().split('T')[0]}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(e.target.value || undefined)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title="Select historical date for BASIC calculations"
                />
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(undefined)}
                    className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                    title="Reset to current date"
                  >
                    Today
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {selectedDate ? `Historical: ${selectedDate}` : 'Current (Latest Data)'}
              </span>
            </div>

            {/* Refresh button */}
            <button
              onClick={refetch}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300
                       rounded-lg hover:bg-gray-50 transition-colors shadow-sm self-end"
              title="Refresh predictions"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Statistics - Compact */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-gray-900">{summary.averagePercentile.toFixed(0)}th</div>
          <div className="text-xs text-gray-500 mt-1">Avg Percentile</div>
        </div>
        <div className="bg-green-100 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-900">{summary.statusCounts.EXCELLENT}</div>
          <div className="text-xs text-green-900 mt-1">Excellent</div>
        </div>
        <div className="bg-blue-100 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-900">{summary.statusCounts.SAFE}</div>
          <div className="text-xs text-blue-900 mt-1">Safe</div>
        </div>
        <div className={`p-3 rounded-lg text-center ${summary.atRisk > 0 ? 'bg-red-100' : 'bg-gray-50'}`}>
          <div className={`text-2xl font-bold ${summary.atRisk > 0 ? 'text-red-900' : 'text-gray-500'}`}>{summary.atRisk}</div>
          <div className={`text-xs mt-1 ${summary.atRisk > 0 ? 'text-red-900' : 'text-gray-500'}`}>At Risk</div>
        </div>
      </div>

      {/* BASIC Score Cards Grid - Tighter layout for all 7 BASICs */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 mb-5 items-start">
        {(Object.keys(BASIC_CATEGORIES) as BasicId[]).map((basicId) => {
          // Support both V1 (predictions) and V2 (basics) response structures
          const basicsData = predictions.basics || predictions.predictions || {};
          const prediction = basicsData[basicId];
          if (!prediction) return null;

          const isExpanded = expandedBasicId === basicId;

          return (
            <div key={basicId} className="flex flex-col">
              <button
                type="button"
                onClick={() => setExpandedBasicId(isExpanded ? null : basicId)}
                className="text-left w-full relative"
                title="Click to view weekly history"
              >
                <BasicScoreCard
                  basicId={basicId}
                  measure={prediction.measure}
                  percentile={prediction.percentile}
                  threshold={prediction.threshold}
                  status={prediction.status}
                  confidenceScore={prediction.confidenceScore}
                  calculatedAt={prediction.calculatedAt}
                  dataSource={prediction.dataSource}
                  isMonitored={prediction.isMonitored}
                  cacheExpires={prediction.cacheExpires}
                />
                <span className="absolute top-2 right-2 text-gray-400">
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </span>
              </button>
              {isExpanded && (
                <BasicHistoryChart
                  dotNumber={dotNumber}
                  basicId={basicId}
                  basicName={getBasicName(basicId)}
                  threshold={prediction.threshold}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Info - Compact */}
      <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
        <strong>Note:</strong> BASIC-14 (Drugs/Alcohol) is never public. BASIC-16 (Crash) from crash DB. BASIC-17 (Hazmat) only for hazmat carriers.
        <span className="ml-3 text-gray-400">Last updated: {new Date().toLocaleString()}</span>
      </div>

      {/* HelpModal */}
      <HelpModal />

      {/* What-If Scenario Builder Section */}
      <div className="mt-12 pt-8 border-t-4 border-gray-200">
        <ScenarioBuilder dotNumber={dotNumber} currentPredictions={predictions} />
      </div>

      {/* Trajectory Visualization Section */}
      <div className="mt-12 pt-8 border-t-4 border-gray-200">
        <TrajectoryVisualization dotNumber={dotNumber} />
      </div>
    </div>
  );
};

export default BasicPredictionsTab;
